import React, { useMemo } from 'react'

import { RollingType } from 'types/GlobalKpiOption'
import { SxProps } from '@mui/material'
import {
  isCustomEconomyMetric,
  ShowPerEnum,
  TMetricSettings
} from 'types/GlobalMetric'
import { CustomPeriodFilter, PeriodFilter } from 'types/GlobalWidget'

import { useAvailableUnits } from 'redux/hooks/AvailableUnits'
import { useMetric } from 'redux/hooks/Metrics'
import { useMetricSegmentAttributeOptions } from 'redux/hooks/AttributeOptions'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Divider from 'components_new/atoms/Divider'

import SearchAndSelectButton from 'components_new/molecules/SearchAndSelectButton'
import ToggleButton from 'components_new/molecules/ToggleButton'

import PeriodSelector from 'components_new/organisms/DashboardUserFilter/PeriodSelector'

interface MetricFooterProps {
  chart: 'bar' | 'line'
  id: string
  onChangeChart: (value: 'bar' | 'line') => void
  onChangeSegmentation?: (segment: string | null) => void
  onChangePeriod: (value: PeriodFilter | CustomPeriodFilter | null) => void
  segmentation?: string | null
  period: PeriodFilter | CustomPeriodFilter | null
  settings: TMetricSettings
  onChangeDistribution?: (dist: ShowPerEnum | null) => void
  onChangeRollingSum?: (rsum: RollingType | null) => void
  onChangeSlices?: (slices: string[]) => void
  onChangeVariant?: (variant: string | null) => void
  onChangeRealEstates?: (values: string[]) => void
  selectedRealEstates?: string[]
  sx?: SxProps
}

const SELECT_BUTTON_MAX_WIDTH = 200

const MetricFooter = (props: MetricFooterProps) => {
  const {
    chart,
    id,
    onChangeChart,
    onChangeDistribution,
    onChangePeriod,
    onChangeRealEstates,
    onChangeRollingSum,
    onChangeSegmentation,
    onChangeSlices,
    onChangeVariant,
    period,
    segmentation,
    selectedRealEstates,
    settings,
    sx
  } = props
  const metric = useMetric(id, settings.variant_kpi_template_id)
  const segmentOptions = useMetricSegmentAttributeOptions(metric?.fact_table_id)
  const availableUnits = useAvailableUnits()
  const { t, lang, translateEnum } = useTranslation()

  const realEstateOptions = useMemo(
    () => availableUnits.map((name) => ({ id: name, title: name })),
    [availableUnits]
  )

  const [distributionOptions, rollingSumOptions, sliceOptions, variantOptions] =
    useMemo(() => {
      const distOpts = (metric?.distributions ?? []).map((dist) => ({
        body: translateEnum('show_pers', dist.enum).title,
        id: dist.enum,
        title: '/ ' + translateEnum('show_pers', dist.enum).abbreviation
      }))

      const rollingOpts = (metric?.rolling_sums ?? []).map((rsum) => ({
        body: translateEnum('rolling_types', rsum).title,
        id: rsum,
        title: translateEnum('rolling_types', rsum).abbreviation
      }))

      const sliceOpts = (metric?.slices.available ?? []).map((s) => ({
        id: s.id,
        title: s.name
      }))

      const vOptions = isCustomEconomyMetric(metric)
        ? metric.variants.map((m) => ({
          id: m.id,
          title: m.title[lang]
        }))
        : []

      return [distOpts, rollingOpts, sliceOpts, vOptions]
    }, [metric])

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: 2,
        height: '100%',
        justifyContent: 'space-between',
        minWidth: 0,
        overflowX: 'auto',
        ...sx
      }}
    >
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
        <ToggleButton
          items={[
            {
              iconName: 'TrendingUpOutlined',
              value: 'line'
            },
            {
              iconName: 'BarChart',
              value: 'bar'
            }
          ]}
          onChange={(_, value) => {
            if (value === 'line' && segmentation) {
              onChangeSegmentation?.(null)
            }

            onChangeChart(value)
          }}
          size="small"
          value={chart}
        />
        <Divider flexItem={true} orientation="vertical" />
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
            {/*-- period --*/}
            <PeriodSelector
              onSubmit={(period: PeriodFilter | CustomPeriodFilter | null) => {
                onChangePeriod(period)
              }}
              periodFilter={period}
              showActive={true}
              size="medium"
            />

            {/*-- segmentation --*/}
            <SearchAndSelectButton
              icon={{ name: 'Segmentation' }}
              multiple={false}
              onChange={(val) => onChangeSegmentation?.(val)}
              options={segmentOptions.map((opt) => ({
                id: opt.id,
                title: opt.name
              }))}
              PopoverProps={{ position: 'top' }}
              size="medium"
              sx={{ maxWidth: SELECT_BUTTON_MAX_WIDTH }}
              title={t('widgets', 'segment_by_title')}
              value={segmentation ?? null}
            />
          </Box>

          {onChangeDistribution ||
          onChangeRollingSum ||
          onChangeVariant ||
          onChangeSlices ||
          onChangeRealEstates ? (
                <>
                  <Divider flexItem={true} orientation="vertical" />

                  <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
                    {/*-- distribution --*/}
                    {onChangeDistribution ? (
                      <SearchAndSelectButton
                        icon={{ name: 'Distribution' }}
                        multiple={false}
                        onChange={(val) =>
                          onChangeDistribution(val as ShowPerEnum | null)
                        }
                        options={distributionOptions}
                        PopoverProps={{ position: 'top' }}
                        size="medium"
                        sx={{ maxWidth: SELECT_BUTTON_MAX_WIDTH }}
                        title={t('metrics', 'distribution')}
                        value={settings.distribution}
                      />
                    ) : null}

                    {/*-- rolling sum --*/}
                    {onChangeRollingSum ? (
                      <SearchAndSelectButton
                        icon={{ name: 'RollingSum' }}
                        multiple={false}
                        onChange={(val) =>
                          onChangeRollingSum(val as RollingType | null)
                        }
                        options={rollingSumOptions}
                        PopoverProps={{ position: 'top' }}
                        size="medium"
                        sx={{ maxWidth: SELECT_BUTTON_MAX_WIDTH }}
                        title={t('metrics', 'rolling_sum')}
                        value={settings.rolling_sum}
                      />
                    ) : null}

                    {/*-- variants --*/}
                    {onChangeVariant && variantOptions.length > 0 ? (
                      <>
                        <SearchAndSelectButton
                          icon={{ name: 'Calculation' }}
                          multiple={false}
                          onChange={(val) => onChangeVariant(val)}
                          options={variantOptions}
                          PopoverProps={{ nullable: false, position: 'top' }}
                          size="medium"
                          sx={{ maxWidth: SELECT_BUTTON_MAX_WIDTH }}
                          title={t('metrics', 'variant')}
                          value={settings.variant_kpi_template_id}
                        />
                      </>
                    ) : null}

                    {/*-- slices --*/}
                    {onChangeSlices ? (
                      <>
                        {metric?.slices.selection_mode === 'single_required' ? (
                          <SearchAndSelectButton
                            icon={{ name: 'Slices' }}
                            multiple={false}
                            onChange={(val) => onChangeSlices(val ? [val] : [])}
                            options={sliceOptions}
                            PopoverProps={{ nullable: false, position: 'top' }}
                            size="medium"
                            sx={{ maxWidth: SELECT_BUTTON_MAX_WIDTH }}
                            title={t('entities', 'slices')}
                            value={settings.slices[0] ?? null}
                          />
                        ) : (
                          <SearchAndSelectButton
                            icon={{ name: 'Slices' }}
                            onChange={(val) => onChangeSlices(val)}
                            options={sliceOptions}
                            PopoverProps={{ position: 'top' }}
                            size="medium"
                            sx={{ maxWidth: SELECT_BUTTON_MAX_WIDTH }}
                            title={t('entities', 'slices')}
                            value={settings.slices}
                          />
                        )}
                      </>
                    ) : null}

                    {/*-- real estates --*/}
                    {onChangeRealEstates ? (
                      <SearchAndSelectButton
                        icon={{ name: 'Filter' }}
                        onChange={(val) => onChangeRealEstates(val)}
                        options={realEstateOptions}
                        PopoverProps={{ position: 'top' }}
                        size="medium"
                        sx={{ maxWidth: SELECT_BUTTON_MAX_WIDTH }}
                        title={t('org_units', 'unit')}
                        value={selectedRealEstates ?? []}
                      />
                    ) : null}
                  </Box>
                </>
              ) : null}
        </Box>
      </Box>
    </Box>
  )
}

export default MetricFooter
